import React, { useState, useEffect, useRef } from 'react';
import WebDataService from './services/webDataService';
import CoverageTable from './components/CoverageTable';
import GraphView from './components/GraphView';
import EditorView from './components/EditorView';
import SettingsView from './components/SettingsView';
import SkillsManagementView from './components/SkillsManagementView';
import SkillDrilldownView from './components/SkillDrilldownView';
import CapabilityDrilldownView from './components/CapabilityDrilldownView';
import PeopleDrilldownView from './components/PeopleDrilldownView';
import DashboardView from './components/DashboardView';
import PrivacyIndicator from './components/PrivacyIndicator';
import LoginPage from './components/LoginPage';
import AuthService from './utils/authService';
import { CoverageCalculator } from './utils/coverageCalculator';

const dataService = new WebDataService();

function App() {
  const [currentView, setCurrentView] = useState('dashboard');
  const [people, setPeople] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataPath, setDataPath] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionRemaining, setSessionRemaining] = useState(0);
  
  const authService = useRef(new AuthService()).current;

  useEffect(() => {
    checkAuthentication();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Set up session monitoring
    const interval = setInterval(() => {
      if (isAuthenticated) {
        const remaining = authService.getSessionRemainingTime();
        setSessionRemaining(remaining);
        
        if (remaining === 0 && isAuthenticated) {
          handleLogout();
        }
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [isAuthenticated, authService]);

  const checkAuthentication = async () => {
    if (authService.isAuthenticated()) {
      setIsAuthenticated(true);
      const remaining = authService.getSessionRemainingTime();
      setSessionRemaining(remaining);
      await initializeApp();
    } else {
      setIsAuthenticated(false);
      setLoading(false);
    }
  };

  const handleAuthentication = async () => {
    setIsAuthenticated(true);
    const remaining = authService.getSessionRemainingTime();
    setSessionRemaining(remaining);
    await initializeApp();
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setCurrentView('dashboard');
    setPeople([]);
    setCapabilities([]);
    setSkills([]);
    setDataPath('');
    setSessionRemaining(0);
  };

  const initializeApp = async () => {
    try {
      setLoading(true);
      const path = await dataService.initialize();
      console.log('Initialized with path:', path); // Debug log
      setDataPath(path || 'No data folder selected');
      
      // Only load data if we have a valid path
      if (path) {
        await loadData();
      }
    } catch (error) {
      console.error('Failed to initialize app:', error);
      setDataPath('No data folder selected');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [loadedPeople, loadedCapabilities, loadedSkillsData] = await Promise.all([
        dataService.loadPeople(),
        dataService.loadCapabilities(),
        dataService.loadSkills()
      ]);
      setPeople(loadedPeople);
      setCapabilities(loadedCapabilities);
      setSkills(loadedSkillsData.skills);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleDataChange = async () => {
    await loadData();
  };

  const handleDataPathChange = async (newPath) => {
    console.log('handleDataPathChange called with:', newPath);
    setDataPath(newPath);
    await loadData();
  };

  const handlePersonDelete = async (personName) => {
    if (window.confirm(`Are you sure you want to delete ${personName}? This action cannot be undone.`)) {
      try {
        await dataService.deletePerson(personName);
        await loadData();
      } catch (error) {
        console.error('Error deleting person:', error);
        alert('Error deleting person. Please try again.');
      }
    }
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return loading ? (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl mb-4">Loading Skills Mapping App...</div>
          <div className="flex items-center justify-center space-x-2 text-green-700 bg-green-50 px-4 py-2 rounded-full border border-green-200">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium">All data stays on your device</span>
          </div>
        </div>
      </div>
    ) : (
      <LoginPage onAuthenticated={handleAuthentication} />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl mb-4">Loading Skills Mapping App...</div>
          <div className="flex items-center justify-center space-x-2 text-green-700 bg-green-50 px-4 py-2 rounded-full border border-green-200">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium">All data stays on your device</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      <nav className="bg-white shadow-sm">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-xl font-semibold text-gray-900">
                  Skills Mapping App
                </h1>
              </div>
              <div className="ml-8 flex space-x-6">
                <button
                  onClick={() => setCurrentView('dashboard')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    currentView === 'dashboard'
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setCurrentView('capabilities')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    currentView === 'capabilities'
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Capabilities
                </button>
                <button
                  onClick={() => setCurrentView('skills')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    currentView === 'skills'
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Skills
                </button>
                <button
                  onClick={() => setCurrentView('people')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    currentView === 'people'
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  People
                </button>
                <button
                  onClick={() => setCurrentView('coverage')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    currentView === 'coverage'
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Coverage Table
                </button>
                <button
                  onClick={() => setCurrentView('graph')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    currentView === 'graph'
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Graph
                </button>
                <button
                  onClick={() => setCurrentView('editor')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    currentView === 'editor'
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Editor
                </button>
                <button
                  onClick={() => setCurrentView('skills-management')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    currentView === 'skills-management'
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Skills Mgmt
                </button>
                <button
                  onClick={() => setCurrentView('settings')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    currentView === 'settings'
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Settings
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <PrivacyIndicator dataService={dataService} dataPath={dataPath} />
              
              {/* Session Info */}
              {sessionRemaining > 0 && (
                <span className="text-xs text-gray-500">
                  Session: {sessionRemaining}m
                </span>
              )}
              
              <span className="text-sm text-gray-500">
                Data: {dataPath}
              </span>
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded border border-red-200 hover:border-red-300 transition-colors"
                title="Sign out"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-1 overflow-y-auto py-6 px-4 sm:px-6 lg:px-8 w-full">
        {currentView === 'dashboard' && (
          <DashboardView
            people={people}
            capabilities={capabilities}
            coverageCalculator={CoverageCalculator}
          />
        )}
        {currentView === 'capabilities' && (
          <CapabilityDrilldownView
            people={people}
            capabilities={capabilities}
            coverageCalculator={CoverageCalculator}
          />
        )}
        {currentView === 'skills' && (
          <SkillDrilldownView
            people={people}
            capabilities={capabilities}
            coverageCalculator={CoverageCalculator}
          />
        )}
        {currentView === 'people' && (
          <PeopleDrilldownView
            people={people}
            capabilities={capabilities}
            coverageCalculator={CoverageCalculator}
          />
        )}
        {currentView === 'coverage' && (
          <CoverageTable
            people={people}
            capabilities={capabilities}
            coverageCalculator={CoverageCalculator}
            onPersonDelete={handlePersonDelete}
          />
        )}
        {currentView === 'graph' && (
          <GraphView
            people={people}
            capabilities={capabilities}
            coverageCalculator={CoverageCalculator}
          />
        )}
        {currentView === 'editor' && (
          <EditorView
            people={people}
            capabilities={capabilities}
            dataService={dataService}
            onDataChange={handleDataChange}
            coverageCalculator={CoverageCalculator}
          />
        )}
        {currentView === 'skills-management' && (
          <SkillsManagementView
            dataService={dataService}
            skills={skills}
            capabilities={capabilities}
            people={people}
            onDataChange={handleDataChange}
          />
        )}
        {currentView === 'settings' && (
          <SettingsView
            dataService={dataService}
            dataPath={dataPath}
            onDataPathChange={handleDataPathChange}
            onDataChange={handleDataChange}
          />
        )}
      </main>
    </div>
  );
}

export default App;